import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useResponsive } from '../../../hooks/useResponsive';
import MobileHackerGames from './MobileHackerGames';


const GamesContainer = styled.div`
  padding: 20px;
  background: rgba(0, 0, 0, 0.95);
  color: #00ff00;
  height: 100%;
  overflow-y: auto;
`;

const GameSelector = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  justify-content: center;
  flex-wrap: wrap;
`;

const GameButton = styled.button<{ active: boolean }>`
  background: ${props => props.active ? 'rgba(0, 255, 0, 0.3)' : 'rgba(0, 255, 0, 0.1)'};
  border: 2px solid ${props => props.active ? '#00ff00' : 'rgba(0, 255, 0, 0.3)'};
  color: #00ff00;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 12px;

  &:hover {
    background: rgba(0, 255, 0, 0.2);
    border-color: #00ff00;
  }
`;

const GameArea = styled.div`
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 12px;
  padding: 30px;
  text-align: center;
`;

// Tic-Tac-Toe Game
const GameBoard = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 5px;
  max-width: 300px;
  margin: 20px auto;
`;

const Cell = styled.button<{ value: string }>`
  width: 80px;
  height: 80px;
  background: rgba(0, 0, 0, 0.8);
  border: 2px solid #00ff00;
  color: ${props => props.value === 'X' ? '#00ff00' : '#ff0000'};
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 255, 0, 0.1);
    box-shadow: 0 0 15px rgba(0, 255, 0, 0.3);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

// Network Scanner Game
const NetworkGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  max-width: 400px;
  margin: 20px auto;
`;

const NetworkNode = styled.button<{ scanned: boolean; vulnerable: boolean }>`
  width: 60px;
  height: 60px;
  background: ${props => 
    props.vulnerable && props.scanned ? 'rgba(255, 0, 0, 0.3)' :
    props.scanned ? 'rgba(0, 255, 0, 0.3)' : 
    'rgba(0, 0, 0, 0.8)'
  };
  border: 2px solid ${props => 
    props.vulnerable && props.scanned ? '#ff0000' :
    props.scanned ? '#00ff00' : 
    'rgba(0, 255, 0, 0.3)'
  };
  color: #ffffff;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.3s ease;
  font-size: 12px;

  &:hover {
    border-color: #00ff00;
  }
`;

// Cryptography Game
const CryptoInput = styled.input`
  background: rgba(0, 0, 0, 0.7);
  border: 2px solid #00ff00;
  color: #00ff00;
  padding: 15px;
  font-size: 18px;
  text-align: center;
  border-radius: 8px;
  margin: 20px;
  font-family: 'Courier New', monospace;
  width: 300px;

  &:focus {
    outline: none;
    box-shadow: 0 0 20px rgba(0, 255, 0, 0.5);
  }
`;

// SQL Injection Game
const SqlTerminal = styled.div`
  background: rgba(0, 0, 0, 0.9);
  border: 1px solid #00ff00;
  border-radius: 8px;
  padding: 20px;
  margin: 20px auto;
  max-width: 500px;
  text-align: left;
  font-family: 'Courier New', monospace;
`;

const SqlInput = styled.input`
  background: transparent;
  border: none;
  color: #00ff00;
  font-family: 'Courier New', monospace;
  width: 100%;
  outline: none;
  font-size: 14px;
`;

// Quiz Game
const QuizContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const QuizQuestion = styled.div`
  background: rgba(0, 255, 0, 0.1);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const QuizOption = styled.button<{ selected: boolean; correct?: boolean }>`
  background: ${props => 
    props.selected && props.correct ? 'rgba(0, 255, 0, 0.3)' :
    props.selected && !props.correct ? 'rgba(255, 0, 0, 0.3)' :
    'rgba(0, 0, 0, 0.5)'
  };
  border: 1px solid ${props => 
    props.selected && props.correct ? '#00ff00' :
    props.selected && !props.correct ? '#ff0000' :
    'rgba(0, 255, 0, 0.3)'
  };
  color: #00ff00;
  padding: 10px 15px;
  margin: 5px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  text-align: left;

  &:hover {
    background: rgba(0, 255, 0, 0.1);
  }
`;

const ActionButton = styled.button`
  background: rgba(0, 255, 0, 0.2);
  border: 2px solid #00ff00;
  color: #00ff00;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  margin: 10px;
  font-weight: bold;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 255, 0, 0.3);
    box-shadow: 0 0 15px rgba(0, 255, 0, 0.4);
  }
`;
const HackerGames: React.FC = () => {
  // Move ALL hooks to the top - before any conditional logic
  const { isMobile } = useResponsive();
  const [currentGame, setCurrentGame] = useState('tictactoe');
  const [board, setBoard] = useState(Array(9).fill(''));
  const [isXNext, setIsXNext] = useState(true);
  const [gameMode, setGameMode] = useState<'human' | 'ai'>('ai');
  const [networkNodes, setNetworkNodes] = useState<Array<{scanned: boolean; vulnerable: boolean}>>([]);
  const [vulnerabilitiesFound, setVulnerabilitiesFound] = useState(0);
  const [scanAttempts, setScanAttempts] = useState(0);
  const [cryptoMessage, setCryptoMessage] = useState('');
  const [cryptoAnswer, setCryptoAnswer] = useState('');
  const [cryptoSolved, setCryptoSolved] = useState(false);
  const [currentCipher, setCurrentCipher] = useState({ encrypted: '', original: '', shift: 0 });
  const [sqlQuery, setSqlQuery] = useState('');
  const [sqlResult, setSqlResult] = useState('');
  const [sqlLevel, setSqlLevel] = useState(1);
  const [currentQuizQuestion, setCurrentQuizQuestion] = useState(0);
  const [quizScore, setQuizScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showQuizResult, setShowQuizResult] = useState(false);

  // All your functions (calculateWinner, minimax, etc.)
  const calculateWinner = (squares: string[]) => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8],
      [0, 3, 6], [1, 4, 7], [2, 5, 8],
      [0, 4, 8], [2, 4, 6]
    ];
    
    for (let line of lines) {
      const [a, b, c] = line;
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return squares[a];
      }
    }
    return null;
  };

  const minimax = (squares: string[], depth: number, isMaximizing: boolean): number => {
    const winner = calculateWinner(squares);
    if (winner === 'O') return 10 - depth;
    if (winner === 'X') return depth - 10;
    if (squares.every(square => square !== '')) return 0;

    if (isMaximizing) {
      let bestScore = -Infinity;
      for (let i = 0; i < 9; i++) {
        if (squares[i] === '') {
          squares[i] = 'O';
          const score = minimax(squares, depth + 1, false);
          squares[i] = '';
          bestScore = Math.max(score, bestScore);
        }
      }
      return bestScore;
    } else {
      let bestScore = Infinity;
      for (let i = 0; i < 9; i++) {
        if (squares[i] === '') {
          squares[i] = 'X';
          const score = minimax(squares, depth + 1, true);
          squares[i] = '';
          bestScore = Math.min(score, bestScore);
        }
      }
      return bestScore;
    }
  };

  const getBestMove = (squares: string[]): number => {
    let bestScore = -Infinity;
    let bestMove = -1;
    
    for (let i = 0; i < 9; i++) {
      if (squares[i] === '') {
        squares[i] = 'O';
        const score = minimax(squares, 0, false);
        squares[i] = '';
        if (score > bestScore) {
          bestScore = score;
          bestMove = i;
        }
      }
    }
    return bestMove;
  };

  const handleCellClick = (index: number) => {
    if (board[index] || calculateWinner(board)) return;
    
    const newBoard = [...board];
    newBoard[index] = isXNext ? 'X' : 'O';
    setBoard(newBoard);
    setIsXNext(!isXNext);

    if (gameMode === 'ai' && isXNext && !calculateWinner(newBoard)) {
      setTimeout(() => {
        const aiMove = getBestMove(newBoard);
        if (aiMove !== -1) {
          const aiBoard = [...newBoard];
          aiBoard[aiMove] = 'O';
          setBoard(aiBoard);
          setIsXNext(true);
        }
      }, 500);
    }
  };

  const resetTicTacToe = () => {
    setBoard(Array(9).fill(''));
    setIsXNext(true);
  };

  const initializeNetwork = () => {
    const nodes = Array(25).fill(null).map(() => ({
      scanned: false,
      vulnerable: Math.random() > 0.7
    }));
    setNetworkNodes(nodes);
    setVulnerabilitiesFound(0);
    setScanAttempts(0);
  };

  const scanNode = (index: number) => {
    if (networkNodes[index]?.scanned) return;
    
    const newNodes = [...networkNodes];
    newNodes[index].scanned = true;
    setNetworkNodes(newNodes);
    setScanAttempts(prev => prev + 1);
    
    if (newNodes[index].vulnerable) {
      setVulnerabilitiesFound(prev => prev + 1);
    }
  };

  const generateCipher = () => {
    const messages = [
      'HELLO HACKER',
      'SECURE SYSTEM',
      'ACCESS GRANTED',
      'CYBER SECURITY',
      'PENETRATION TEST'
    ];
    const message = messages[Math.floor(Math.random() * messages.length)];
    const shift = Math.floor(Math.random() * 25) + 1;
    
    const encrypted = message.split('').map(char => {
      if (char === ' ') return ' ';
      const code = char.charCodeAt(0) - 65;
      const shifted = (code + shift) % 26;
      return String.fromCharCode(shifted + 65);
    }).join('');
    
    setCurrentCipher({ encrypted, original: message, shift });
    setCryptoAnswer('');
    setCryptoSolved(false);
  };

  const checkCryptoAnswer = () => {
    if (cryptoAnswer.toUpperCase() === currentCipher.original) {
      setCryptoSolved(true);
    }
  };

  const handleSqlQuery = () => {
    const query = sqlQuery.toLowerCase();
    if (query.includes("' or '1'='1")) {
      setSqlResult('🎉 SQL Injection successful! You bypassed authentication.');
    } else if (query.includes('union select')) {
      setSqlResult('🎯 UNION attack detected! Data extracted.');
    } else if (query.includes('drop table')) {
      setSqlResult('💀 Table dropped! Destructive attack successful.');
    } else {
      setSqlResult('❌ Query failed. Try a different injection technique.');
    }
  };

  const quizQuestions = [
    {
      question: "What does SQL injection exploit?",
      options: ["Network protocols", "Database queries", "File systems", "Memory buffers"],
      correct: 1
    },
    {
      question: "Which tool is commonly used for network scanning?",
      options: ["Wireshark", "Nmap", "Burp Suite", "John the Ripper"],
      correct: 1
    },
    {
      question: "What is the default SSH port?",
      options: ["21", "22", "23", "25"],
      correct: 1
    },
    {
      question: "What does XSS stand for?",
      options: ["Cross-Site Scripting", "XML Security Scanner", "eXternal System Service", "eXtended SQL Syntax"],
      correct: 0
    }
  ];

  const handleQuizAnswer = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    setShowQuizResult(true);
    
    if (answerIndex === quizQuestions[currentQuizQuestion].correct) {
      setQuizScore(prev => prev + 1);
    }
    
    setTimeout(() => {
      if (currentQuizQuestion < quizQuestions.length - 1) {
        setCurrentQuizQuestion(prev => prev + 1);
        setSelectedAnswer(null);
        setShowQuizResult(false);
      }
    }, 2000);
  };

  const resetQuiz = () => {
    setCurrentQuizQuestion(0);
    setQuizScore(0);
    setSelectedAnswer(null);
    setShowQuizResult(false);
  };

  // useEffect hook
  useEffect(() => {
    if (currentGame === 'network' && networkNodes.length === 0) {
      initializeNetwork();
    }
    if (currentGame === 'crypto' && !currentCipher.encrypted) {
      generateCipher();
    }
  }, [currentGame, networkNodes.length, currentCipher.encrypted]);

  // NOW you can do the conditional return AFTER all hooks
  if (isMobile) {
    return <MobileHackerGames />;
  }

  // All your render functions
  const renderTicTacToe = () => {
    const winner = calculateWinner(board);
    return (
      <div>
        <h2>🎮 AI Tic-Tac-Toe</h2>
        <p style={{ color: '#00ffff', fontSize: '18px' }}>
          {winner ? `🏆 Winner: ${winner}` : `Your turn: X vs AI: O`}
        </p>
        <GameBoard>
          {board.map((cell, index) => (
            <Cell
              key={index}
              value={cell}
              onClick={() => handleCellClick(index)}
              disabled={!!cell || !!winner || (!isXNext && gameMode === 'ai')}
            >
              {cell}
            </Cell>
          ))}
        </GameBoard>
        <ActionButton onClick={resetTicTacToe}>
          🔄 New Game
        </ActionButton>
      </div>
    );
  };

  const renderNetworkScanner = () => (
    <div>
      <h2>🌐 Network Scanner</h2>
      <p style={{ color: '#00ffff' }}>Scan the network to find vulnerabilities!</p>
      <p style={{ color: '#cccccc' }}>
        Vulnerabilities Found: {vulnerabilitiesFound} | Scans: {scanAttempts}
      </p>
      <NetworkGrid>
        {networkNodes.map((node, index) => (
          <NetworkNode
            key={index}
            scanned={node.scanned}
            vulnerable={node.vulnerable}
            onClick={() => scanNode(index)}
          >
            {node.scanned ? (node.vulnerable ? '🚨' : '✅') : '❓'}
          </NetworkNode>
        ))}
      </NetworkGrid>
      <ActionButton onClick={initializeNetwork}>
        🔄 New Network
      </ActionButton>
    </div>
  );

  const renderCryptography = () => (
    <div>
      <h2>🔐 Caesar Cipher Decoder</h2>
      <p style={{ color: '#00ffff' }}>Decrypt the message!</p>
      <div style={{ background: 'rgba(0,0,0,0.7)', padding: '15px', margin: '20px', borderRadius: '8px' }}>
        <strong>Encrypted: {currentCipher.encrypted}</strong>
      </div>
      <CryptoInput
        value={cryptoAnswer}
        onChange={(e) => setCryptoAnswer(e.target.value)}
        placeholder="Enter decrypted message..."
        onKeyPress={(e) => e.key === 'Enter' && checkCryptoAnswer()}
      />
      <br />
      <ActionButton onClick={checkCryptoAnswer}>
        🔍 Decrypt
      </ActionButton>
      <ActionButton onClick={generateCipher}>
        🎲 New Cipher
      </ActionButton>
      {cryptoSolved && (
        <div style={{ color: '#00ff00', fontSize: '20px', marginTop: '20px' }}>
          🎉 Decrypted! Original: {currentCipher.original}
        </div>
      )}
    </div>
  );

  const renderSqlInjection = () => (
    <div>
      <h2>💉 SQL Injection Lab</h2>
      <p style={{ color: '#00ffff' }}>Try to bypass the login system!</p>
      <SqlTerminal>
        <div style={{ marginBottom: '10px' }}>
          mysql&gt; SELECT * FROM users WHERE username='admin' AND password='
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <SqlInput
            value={sqlQuery}
            onChange={(e) => setSqlQuery(e.target.value)}
            placeholder="Enter your injection here..."
            onKeyPress={(e) => e.key === 'Enter' && handleSqlQuery()}
          />
          <span>';</span>
        </div>
      </SqlTerminal>
      <ActionButton onClick={handleSqlQuery}>
        💻 Execute Query
      </ActionButton>
      {sqlResult && (
        <div style={{ 
          marginTop: '20px', 
          padding: '15px', 
          background: 'rgba(0,0,0,0.7)', 
          borderRadius: '8px',
          color: sqlResult.includes('🎉') || sqlResult.includes('🎯') ? '#00ff00' : '#ff6666'
        }}>
          {sqlResult}
        </div>
      )}
    </div>
  );

  const renderQuiz = () => (
    <div>
      <h2>🧠 Cybersecurity Quiz</h2>
      <p style={{ color: '#00ffff' }}>
        Question {currentQuizQuestion + 1} of {quizQuestions.length} | Score: {quizScore}
      </p>
      {currentQuizQuestion < quizQuestions.length ? (
        <QuizContainer>
          <QuizQuestion>
            <h3 style={{ marginBottom: '20px' }}>
              {quizQuestions[currentQuizQuestion].question}
            </h3>
            {quizQuestions[currentQuizQuestion].options.map((option, index) => (
              <QuizOption
                key={index}
                selected={selectedAnswer === index}
                correct={showQuizResult && index === quizQuestions[currentQuizQuestion].correct}
                onClick={() => !showQuizResult && handleQuizAnswer(index)}
              >
                {option}
              </QuizOption>
            ))}
          </QuizQuestion>
        </QuizContainer>
      ) : (
        <div style={{ color: '#00ff00', fontSize: '24px' }}>
          🎉 Quiz Complete! Final Score: {quizScore}/{quizQuestions.length}
          <br />
          <ActionButton onClick={resetQuiz}>
            🔄 Restart Quiz
          </ActionButton>
        </div>
      )}
    </div>
  );

  // Desktop render
  return (
    <GamesContainer>
      <h1 style={{ textAlign: 'center', color: '#00ffff', marginBottom: '30px' }}>
        🎮 Cybersecurity Games Arcade
      </h1>
      
      <GameSelector>
        <GameButton 
          active={currentGame === 'tictactoe'} 
          onClick={() => setCurrentGame('tictactoe')}
        >
          🎯 AI Tic-Tac-Toe
        </GameButton>
        <GameButton 
          active={currentGame === 'network'} 
          onClick={() => setCurrentGame('network')}
        >
          🌐 Network Scanner
        </GameButton>
        <GameButton 
          active={currentGame === 'crypto'} 
          onClick={() => setCurrentGame('crypto')}
        >
          🔐 Crypto Decoder
        </GameButton>
        <GameButton 
          active={currentGame === 'sql'} 
          onClick={() => setCurrentGame('sql')}
        >
          💉 SQL Injection
        </GameButton>
        <GameButton 
          active={currentGame === 'quiz'} 
          onClick={() => setCurrentGame('quiz')}
        >
          🧠 Cyber Quiz
        </GameButton>
      </GameSelector>

      <GameArea>
        {currentGame === 'tictactoe' && renderTicTacToe()}
        {currentGame === 'network' && renderNetworkScanner()}
        {currentGame === 'crypto' && renderCryptography()}
        {currentGame === 'sql' && renderSqlInjection()}
        {currentGame === 'quiz' && renderQuiz()}
      </GameArea>
    </GamesContainer>
  );
};

export default HackerGames;
